import { memo } from "react";
import classNames from "classnames";
import { PlusIcon } from "@icons";
import { usePickerRoot } from "./PickerContext";
import {
    PickerCellTrigger,
    type PickerCellContentProps,
} from "./PickerCellTrigger";
import styles from "./PickerCellTrigger.module.scss";

const PickerCellContentImpl = ({
    value,
    addText = "Add",
    hideAddIcon = false,
    showDisabledAdd = false,
    readOnly = false,
    className,
    ...triggerProps
}: PickerCellContentProps) => {
    const root = usePickerRoot();
    const isEmpty =
        (value === undefined || value === null) && root.selectionLabel === null;

    if (isEmpty && readOnly && !showDisabledAdd) {
        return (
            <PickerCellTrigger
                {...triggerProps}
                readOnly
                className={className}
                placeholder={null}
            />
        );
    }

    return (
        <PickerCellTrigger
            {...triggerProps}
            readOnly={readOnly}
            className={classNames(className, {
                [styles.cellTriggerDisabledAdd]: isEmpty && readOnly,
            })}
            value={
                isEmpty ? (
                    <span className={styles.cellAdd} data-picker-cell-add="">
                        {!hideAddIcon && (
                            <PlusIcon
                                className={styles.cellAddIcon}
                                aria-hidden
                            />
                        )}
                        {addText}
                    </span>
                ) : (
                    value
                )
            }
        />
    );
};

/**
 * `Picker.CellTrigger` with the editable-cell empty state: an "Add" row with
 * a leading plus glyph until the root has a value. The "Add" keeps its space
 * but stays transparent until the trigger is hovered, keyboard-focused or
 * open; a host table reveals it for its hovered, active or focused row
 * through `[data-picker-cell-add]`. An empty `readOnly` cell renders nothing
 * unless `showDisabledAdd` keeps a disabled "Add", which is always visible.
 * Memoised like `Picker.CellTrigger`.
 */
export const PickerCellContent = memo(PickerCellContentImpl);
